import { Box } from '@material-ui/core'
import { useState } from 'react'
import { FlexBox, Wrap } from 'shared/components/Base/Base'
import { Button } from 'shared/components/Button/Button'
import { SimpleDialog } from 'shared/components/Dialog/SimpleDialog'
import { Text } from 'shared/components/Text/Text'
import { darkGray, lightGray1 } from 'shared/theme/colors'

import { formatCurrency } from '../../../helpers/formatters'
import {
  DeductionConfig,
  DeductionConfigStatus,
  Repayment,
  VendorProgramPolicyType,
} from '../../../models/repayments'
import { useUserProvider } from '../../Context/UserContext'
import { PolicyActivityItem } from './PolicyActivityItem'

interface PolicyCorrectionProps {
  policy: Repayment
  defaultDeductionConfig?: DeductionConfig
  config: DeductionConfig
  onCancelConfig: (id: string) => Promise<any>
}

function CorrectionText({
  description,
  from,
  to,
}: {
  description: string
  from?: string | number | null
  to?: string | number | null
}) {
  if (!from || !to) return null

  // Only show the correction if the values are different
  if (from === to) {
    return null
  }

  return (
    <FlexBox gap="20px">
      <Text variant="header3" fontColor={lightGray1}>
        {description}
      </Text>
      <Text variant="header3" fontColor={darkGray}>
        Changed from {from} to {to}
      </Text>
    </FlexBox>
  )
}

export function PolicyCorrectionDetails({
  policy,
  defaultDeductionConfig,
  config,
  onCancelConfig,
}: PolicyCorrectionProps) {
  const { showBanner } = useUserProvider()
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const policyType = policy.repayment_details.policy_type

  const onCancel = async () => {
    setIsLoading(true)
    try {
      await onCancelConfig(config.id)
      setIsLoading(false)
      setCancelDialogOpen(false)
    } catch (error: any) {
      setIsLoading(false)
      setCancelDialogOpen(false)
      if (
        error.response.data?.type === 'client_error' &&
        error.response.data.errors &&
        error.response.data.errors.every((e: any) => !!e.detail)
      )
        showBanner('error', {
          message: error.response.data.errors.map((e: { code: string; detail: string }) => e.detail),
        })
      else if (error.response.data.detail) showBanner('error', { message: error.response.data.detail })
      else showBanner('error', { message: `An error occurred: ${JSON.stringify(error.response.data)}` })
    }
  }

  const defaultConfigBillingPeriods =
    defaultDeductionConfig?.num_periods ??
    Math.ceil(
      Number(defaultDeductionConfig?.advance_total_billing_amount) /
        Number(defaultDeductionConfig?.advance_periodic_amount)
    )
  const deductionConfigBillingPeriods =
    config.num_periods ??
    Math.ceil(Number(config?.advance_total_billing_amount) / Number(config.advance_periodic_amount))

  return (
    <>
      <PolicyActivityItem
        config={config}
        cta={
          config.status === DeductionConfigStatus.SCHEDULED && (
            <Button
              variant="text"
              onClick={(e) => {
                e.stopPropagation()
                setCancelDialogOpen(true)
              }}
            >
              Cancel deduction correction
            </Button>
          )
        }
      >
        <FlexBox isColumn gap="8px">
          {policyType === VendorProgramPolicyType.ADVANCE && (
            <>
              <CorrectionText
                description="Remaining payments"
                from={formatCurrency(
                  defaultDeductionConfig?.advance_total_billing_amount ??
                    policy.repayment_details.underpayment_from_previous_periods
                )}
                to={formatCurrency(config.advance_total_billing_amount)}
              />
              <CorrectionText
                description="Weekly deduction"
                from={formatCurrency(defaultDeductionConfig?.advance_periodic_amount ?? null)}
                to={formatCurrency(config.advance_periodic_amount)}
              />
              <CorrectionText
                description="Billing periods"
                from={
                  isNaN(defaultConfigBillingPeriods) ? undefined : defaultConfigBillingPeriods?.toString()
                }
                to={
                  isNaN(deductionConfigBillingPeriods) ? undefined : deductionConfigBillingPeriods?.toString()
                }
              />
            </>
          )}
          {policyType === VendorProgramPolicyType.SUBSCRIPTION && (
            <>
              <CorrectionText
                description="Remaining payments"
                from={
                  defaultDeductionConfig?.subscription_accrued_billing_amount ??
                  policy.repayment_details.underpayment_from_previous_periods
                }
                to={config.subscription_accrued_billing_amount}
              />
              <CorrectionText
                description="Weekly deduction"
                from={
                  defaultDeductionConfig?.subscription_periodic_amount ??
                  policy.repayment_details.amount_owed_per_period
                }
                to={config.subscription_periodic_amount}
              />
            </>
          )}
        </FlexBox>
      </PolicyActivityItem>

      <SimpleDialog isOpen={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <Wrap width="380px" maxWidth="100%">
          <Text variant="header2" align="left">
            Cancel deduction correction?
          </Text>

          <Box mt={9} />
          <Button margin="0 auto" variant="primary" size="large" onClick={onCancel} isLoading={isLoading}>
            Confirm
          </Button>
          <Box m={3} />
          <Button margin="0 auto" variant="text" onClick={() => setCancelDialogOpen(false)}>
            Don't cancel
          </Button>
        </Wrap>
      </SimpleDialog>
    </>
  )
}
